import type { BillingState, ContractState, Project } from '@/types';
import { formatCurrency, formatDate, parseDate } from '@/lib/format';

export { formatCurrency, formatDate };

export const BILLING_ALERT_DAYS = 7;
export const CONTRACT_ALERT_DAYS = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate();
}

/** Devuelve la próxima fecha de cobro mensual a partir del día configurado. */
export function getNextBillingDate(
  billingDay?: number | null,
  from: Date = new Date(),
): Date | null {
  if (!billingDay || billingDay < 1) return null;
  const today = startOfDay(from);
  const year = today.getFullYear();
  const month = today.getMonth();

  const thisMonthDay = Math.min(billingDay, daysInMonth(year, month));
  const thisMonth = new Date(year, month, thisMonthDay);

  if (thisMonth.getTime() >= today.getTime()) return thisMonth;

  const nextMonthIndex = month + 1;
  const nextYear = nextMonthIndex > 11 ? year + 1 : year;
  const normalizedMonth = nextMonthIndex % 12;
  const nextDay = Math.min(billingDay, daysInMonth(nextYear, normalizedMonth));
  return new Date(nextYear, normalizedMonth, nextDay);
}

/**
 * Indica si el período de cobro que comienza en `cycleStart` ya fue pagado,
 * considerando los recibos existentes del proyecto.
 */
function isCyclePaid(
  billingDay: number,
  cycleStart: Date,
  receiptDates: string[] | null | undefined,
): boolean {
  if (!receiptDates || receiptDates.length === 0) return false;
  const startMs = startOfDay(cycleStart).getTime();
  const nextMonth = cycleStart.getMonth() + 1;
  const endDay = Math.min(billingDay, daysInMonth(cycleStart.getFullYear(), nextMonth));
  const endMs = startOfDay(new Date(cycleStart.getFullYear(), nextMonth, endDay)).getTime();

  return receiptDates.some((value) => {
    const date = parseDate(value);
    if (!date) return false;
    const time = startOfDay(date).getTime();
    return time >= startMs && time < endMs;
  });
}

/**
 * Determina el estado del cobro del ciclo mensual actual:
 * - `hoy`: la fecha de cobro es justamente hoy.
 * - `pendiente`: faltan 7 días o menos para la fecha de cobro (ventana previa).
 * - `pagado`: el cobro del período ya fue registrado (existe un recibo).
 * - `normal`: aún falta más de una semana y no corresponde al período cobrado.
 */
export function resolveBillingState(
  billingDay: number | null | undefined,
  receiptDates: string[] | null | undefined,
  now: Date = new Date(),
): BillingState {
  if (!billingDay || billingDay < 1) return 'normal';

  const today = startOfDay(now);
  const nextBilling = getNextBillingDate(billingDay, now);
  const daysToBilling = nextBilling ? daysBetween(now, nextBilling) : null;

  if (daysToBilling !== null && daysToBilling <= 0) return 'hoy';
  if (daysToBilling !== null && daysToBilling <= BILLING_ALERT_DAYS) return 'pendiente';

  // Después de la fecha de cobro del mes en curso: verificar si fue registrado.
  const thisMonthBilling = new Date(
    today.getFullYear(),
    today.getMonth(),
    Math.min(billingDay, daysInMonth(today.getFullYear(), today.getMonth())),
  );
  if (today.getTime() >= thisMonthBilling.getTime() && isCyclePaid(billingDay, thisMonthBilling, receiptDates)) {
    return 'pagado';
  }

  return 'normal';
}

/** Diferencia en días entre dos fechas (b - a). */
export function daysBetween(a: Date, b: Date): number {
  return Math.round((startOfDay(b).getTime() - startOfDay(a).getTime()) / DAY_MS);
}

export interface ProjectAlerts {
  nextBillingDate: Date | null;
  billingState: BillingState;
  billingSoon: boolean;
  daysToBilling: number | null;
  contractState: ContractState;
  daysToContractEnd: number | null;
  hasAlert: boolean;
}

/**
 * Calcula todas las alertas de seguimiento de un proyecto según la fecha actual.
 * `receiptDates` permite reconocer el estado PAGADO a partir de los recibos del proyecto.
 */
export function getProjectAlerts(
  project: Project,
  now: Date = new Date(),
  receiptDates?: string[] | null,
): ProjectAlerts {
  const nextBillingDate = getNextBillingDate(project.billing_day, now);
  const daysToBilling = nextBillingDate ? daysBetween(now, nextBillingDate) : null;
  const billingState = resolveBillingState(project.billing_day, receiptDates, now);
  const billingSoon = billingState === 'pendiente' || billingState === 'hoy';

  let contractState: ContractState = 'vigente';
  let daysToContractEnd: number | null = null;

  const end = parseDate(project.contract_end);
  if (end) {
    daysToContractEnd = daysBetween(now, end);
    if (daysToContractEnd < 0) contractState = 'finalizado';
    else if (daysToContractEnd <= CONTRACT_ALERT_DAYS) contractState = 'por_finalizar';
  }

  const contractAlert = contractState === 'por_finalizar' || contractState === 'finalizado';

  return {
    nextBillingDate,
    billingState,
    billingSoon,
    daysToBilling,
    contractState,
    daysToContractEnd,
    hasAlert: billingSoon || contractAlert,
  };
}

export const BILLING_STATE_LABEL: Record<BillingState, string> = {
  normal: 'Al día',
  pendiente: 'COBRO PENDIENTE',
  hoy: 'SE COBRA HOY',
  pagado: 'PAGADO',
};